import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Star, MessageSquare, Trash2, Phone, QrCode, X, Download, Printer } from 'lucide-react';
import QRCode from 'react-qr-code';
import { clsx } from 'clsx';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { SkeletonTable, EmptyState } from '../components/LoadingStates';
import { feedbackService } from '../services/feedback';
import { formatDateTime } from './inventory/inventoryUtils';

const Stars: React.FC<{ rating: number; size?: number }> = ({ rating, size = 14 }) => (
  <span className="inline-flex items-center">
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} style={{ width: size, height: size }} className={i <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
    ))}
  </span>
);

export const FeedbackPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const org = user?.organizationCode;
  const restaurantId = getActiveRestaurantId() || undefined;
  const [minRating, setMinRating] = useState<number | undefined>(undefined);
  const [confirm, setConfirm] = useState<{ open: boolean; id: string }>({ open: false, id: '' });
  const [showQr, setShowQr] = useState(false);
  const qrPrintRef = useRef<HTMLDivElement>(null);
  const feedbackUrl = restaurantId ? `${window.location.origin}/feedback/${restaurantId}` : null;

  const handleDownloadQr = () => {
    const svgEl = qrPrintRef.current?.querySelector('svg');
    if (!svgEl) return;
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'feedback-qr.svg';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrintQr = () => {
    if (!qrPrintRef.current) return;
    const win = window.open('', '_blank', 'width=400,height=520');
    if (!win) return;
    win.document.write(`
      <html><head><title>Feedback QR Code</title>
      <style>body{font-family:sans-serif;text-align:center;padding:30px;}h2{margin:0 0 8px;}p{color:#666;margin:4px 0;font-size:13px;word-break:break-all;}</style>
      </head><body>${qrPrintRef.current.innerHTML}</body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const { data: summary } = useQuery(['feedback-summary', org, restaurantId], () => feedbackService.summary(org!, restaurantId), { enabled: !!org });
  const { data: feedback, isLoading } = useQuery(
    ['feedback', org, restaurantId, minRating],
    () => feedbackService.list(org!, restaurantId, minRating),
    { enabled: !!org },
  );

  const deleteMutation = useMutation(feedbackService.remove, {
    onSuccess: () => { queryClient.invalidateQueries(['feedback']); queryClient.invalidateQueries(['feedback-summary']); toast.success('Feedback deleted'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); },
  });

  if (!org) {
    return <div className="p-6"><EmptyState icon={<MessageSquare className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />} title="No organization selected" description="Your account is not linked to an organization." /></div>;
  }

  const total = summary?.count ?? 0;
  const dist = summary?.distribution ?? [0, 0, 0, 0, 0];

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-3.5 mb-6">
        <div className="grid place-items-center h-12 w-12 rounded-2xl text-white shrink-0" style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 14px 32px -12px ${currentTheme.colors.primary[500]}` }}>
          <MessageSquare className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: currentTheme.colors.text.primary }}>Customer Feedback</h1>
          <p style={{ color: currentTheme.colors.text.secondary }}>Ratings and reviews from your guests</p>
        </div>
        <button
          onClick={() => setShowQr(true)}
          disabled={!feedbackUrl}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white shrink-0 disabled:opacity-50"
          style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})` }}
          title={feedbackUrl ? 'Show the customer feedback QR code' : 'Select a restaurant first'}
        >
          <QrCode className="h-4 w-4" />
          <span className="hidden sm:inline">Feedback QR</span>
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-5 flex items-center gap-4" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
          <div className="text-center">
            <div className="text-4xl font-bold" style={{ color: currentTheme.colors.text.primary }}>{summary?.averageRating?.toFixed(1) ?? '—'}</div>
            <Stars rating={Math.round(summary?.averageRating ?? 0)} size={16} />
            <div className="text-xs mt-1" style={{ color: currentTheme.colors.text.secondary }}>{total} review{total === 1 ? '' : 's'}</div>
          </div>
        </div>
        <div className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-5 lg:col-span-2" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
          {[5, 4, 3, 2, 1].map(star => {
            const count = dist[star - 1] || 0;
            const pct = total > 0 ? (count / total) * 100 : 0;
            return (
              <div key={star} className="flex items-center gap-3 mb-1.5">
                <span className="text-xs w-8 flex items-center gap-0.5" style={{ color: currentTheme.colors.text.secondary }}>{star}<Star className="h-3 w-3 fill-amber-400 text-amber-400" /></span>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: currentTheme.colors.background.hover }}>
                  <div className="h-full rounded-full bg-amber-400" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs w-8 text-right" style={{ color: currentTheme.colors.text.secondary }}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Filter:</span>
        {[undefined, 4, 3, 2].map((r, i) => (
          <button key={i} onClick={() => setMinRating(r)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border"
            style={{
              backgroundColor: minRating === r ? currentTheme.colors.primary[600] : currentTheme.colors.background.card,
              color: minRating === r ? '#fff' : currentTheme.colors.text.secondary,
              borderColor: minRating === r ? currentTheme.colors.primary[600] : currentTheme.colors.border.primary,
            }}>
            {r === undefined ? 'All' : `${r}★ & up`}
          </button>
        ))}
      </div>

      {isLoading ? (
        <SkeletonTable rows={6} columns={3} />
      ) : !feedback || feedback.length === 0 ? (
        <EmptyState icon={<MessageSquare className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />} title="No feedback yet" description="Reviews submitted from the QR menu will appear here." />
      ) : (
        <div className="space-y-3">
          {feedback.map(f => (
            <div key={f.id} className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-4" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Stars rating={f.rating} />
                    {f.source && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 uppercase">{f.source}</span>}
                  </div>
                  {f.comment && <p className="text-sm mt-2" style={{ color: currentTheme.colors.text.primary }}>{f.comment}</p>}
                  <div className="text-xs mt-2 flex items-center gap-3 flex-wrap" style={{ color: currentTheme.colors.text.secondary }}>
                    {f.customerName && <span>{f.customerName}</span>}
                    {f.customerMobile && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {f.customerMobile}</span>}
                    <span>{formatDateTime(f.createdAt)}</span>
                  </div>
                </div>
                <button onClick={() => setConfirm({ open: true, id: f.id })} className="p-2 rounded-lg flex-shrink-0" style={{ color: '#dc2626', backgroundColor: '#fef2f2' }}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmationModal
        isOpen={confirm.open}
        onClose={() => setConfirm({ open: false, id: '' })}
        onConfirm={() => { deleteMutation.mutate(confirm.id); setConfirm({ open: false, id: '' }); }}
        title="Delete Feedback" message="Delete this review? This cannot be undone." confirmText="Delete" type="danger" isLoading={deleteMutation.isLoading}
      />

      {/* Customer feedback QR */}
      {showQr && feedbackUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowQr(false)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Customer Feedback QR</h2>
              <button onClick={() => setShowQr(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            <div ref={qrPrintRef} className="flex flex-col items-center">
              <h2 style={{ margin: '0 0 8px', fontSize: 18, textAlign: 'center' }}>Scan to share your feedback</h2>
              <div style={{ background: '#fff', padding: 16, borderRadius: 12, border: '1px solid #e5e7eb' }}>
                <QRCode value={feedbackUrl} size={200} />
              </div>
              <p style={{ marginTop: 12, fontSize: 12, color: '#6b7280', textAlign: 'center', wordBreak: 'break-all', maxWidth: 260 }}>{feedbackUrl}</p>
            </div>

            <p className="text-xs text-gray-500 text-center mt-3">
              Print this on the bill or a table tent. Guests scan it to rate their visit — no app or login needed.
            </p>

            <div className="flex gap-3 mt-5">
              <button onClick={handleDownloadQr} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50">
                <Download className="h-4 w-4" /> Download
              </button>
              <button onClick={handlePrintQr} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})` }}>
                <Printer className="h-4 w-4" /> Print
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
